import React, { useState, useEffect } from 'react';
import css from './OurCategories.module.css';

// Animated category GIF assets from src/assets/categories
import kidsCard from '../../../../../assets/categories/e43c2986d9d9fdef01c5d0512b61eabee3dfb518.gif';
import msaCard from '../../../../../assets/categories/b51d1939070465f899ca17578dfa7e5c9dbe1970.gif';
import schoolCard from '../../../../../assets/categories/2bf99922d2ee03b76c1d386cc08923bd549d6218.gif';
import workCard from '../../../../../assets/categories/38c183e16a79504fd45a562958e8373bf508db17.gif';
import storiesCard from '../../../../../assets/categories/403986934349598d7c8225aad468daf9126f5cc9.gif';
import quranicCard from '../../../../../assets/categories/18df4930f6b3ff8ab4922cae7252131b9c972c85.gif';
import scratchCard from '../../../../../assets/categories/a8a987b04d9ff91825b09104ee633c84bc989cfc.gif';
import { FormattedMessage } from '../../../../../util/reactIntl';
import { NamedLink } from '../../../../../components';
import { stringify } from '../../../../../util/urlHelpers';

// The 7 official Arabibi teaching categories
const ALL_CATEGORIES = [
  {
    name: 'kids',
    searchOption: 'arabic_for_kids',
    titleId: 'OurCategories.category1.title',
    descriptionId: 'OurCategories.category1.description',
    image: kidsCard,
    bg: '#FFF4E1',
  },
  {
    name: 'msa',
    searchOption: 'modern_standard_arabic_msa',
    titleId: 'OurCategories.category2.title',
    descriptionId: 'OurCategories.category2.description',
    image: msaCard,
    bg: '#FFF9EE',
  },
  {
    name: 'school',
    searchOption: 'arabic_for_school',
    titleId: 'OurCategories.category3.title',
    descriptionId: 'OurCategories.category3.description',
    image: schoolCard,
    bg: '#FFF9EE',
  },
  {
    name: 'work',
    searchOption: 'arabic_for_work',
    titleId: 'OurCategories.category4.title',
    descriptionId: 'OurCategories.category4.description',
    image: workCard,
    bg: '#FFF4E1',
  },
  {
    name: 'stories',
    searchOption: 'stories_and_songs',
    titleId: 'OurCategories.category5.title',
    descriptionId: 'OurCategories.category5.description',
    image: storiesCard,
    bg: '#FFF9EE',
  },
  {
    name: 'quranic',
    searchOption: 'quranic_arabic',
    titleId: 'OurCategories.category6.title',
    descriptionId: 'OurCategories.category6.description',
    image: quranicCard,
    bg: '#FFF4E1',
  },
  {
    name: 'scratch',
    searchOption: 'arabic_from_scratch',
    titleId: 'OurCategories.categoryScratch.title',
    descriptionId: 'OurCategories.categoryScratch.description',
    image: scratchCard,
    bg: '#FFF9EE',
  },
];

function OurCategories() {
  const getInitialAudience = () => {
    if (typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search);
        const urlAudience = params.get('audience');
        if (urlAudience && ['kids', 'adults', 'tutors'].includes(urlAudience.toLowerCase())) {
          return urlAudience.toLowerCase();
        }
        const saved = sessionStorage.getItem('arabibi_audience');
        if (saved && ['kids', 'adults', 'tutors'].includes(saved.toLowerCase())) {
          return saved.toLowerCase();
        }
      } catch (e) {
        // Fall back gracefully
      }
    }
    return 'kids';
  };

  const [audience, setAudience] = useState(getInitialAudience);

  useEffect(() => {
    const onAudienceChanged = e => {
      if (e?.detail?.audience) {
        setAudience(e.detail.audience);
      }
    };
    const onPopState = () => {
      setAudience(getInitialAudience());
    };
    window.addEventListener('arabibi-audience-changed', onAudienceChanged);
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('arabibi-audience-changed', onAudienceChanged);
      window.removeEventListener('popstate', onPopState);
    };
  }, []);

  // For adult learners, reorder: MSA, Arabic for Work, and Arabic from Scratch are placed first (Item 2.5)
  const displayedCategories =
    audience === 'adults'
      ? [
          ALL_CATEGORIES.find(c => c.name === 'msa'),
          ALL_CATEGORIES.find(c => c.name === 'work'),
          ALL_CATEGORIES.find(c => c.name === 'scratch'),
          ALL_CATEGORIES.find(c => c.name === 'quranic'),
          ALL_CATEGORIES.find(c => c.name === 'stories'),
          ALL_CATEGORIES.find(c => c.name === 'kids'),
          ALL_CATEGORIES.find(c => c.name === 'school'),
        ].filter(Boolean)
      : ALL_CATEGORIES;

  return (
    <section className={css.section}>
      <div className={css.header}>
        <h2 className={css.title}>
          <FormattedMessage id="OurCategories.title" />
        </h2>
        <p className={css.subtitle}>
          <FormattedMessage id="OurCategories.subtitle" />
        </p>
      </div>

      <div className={css.grid}>
        {displayedCategories.map((cat, i) => (
          <div
            key={cat.name || i}
            className={`${css.card} ${css[cat.name]}`}
            style={{ backgroundColor: cat.bg }}
          >
            <div className={css.cardContent}>
              <h3 className={css.cardTitle}>
                <FormattedMessage id={cat.titleId} />
              </h3>
              <p className={css.cardDesc}>
                <FormattedMessage id={cat.descriptionId} />
              </p>
              <NamedLink
                name="SearchPage"
                to={{
                  search: `?${stringify({
                    pub_teaching_categories: `has_any:${cat.searchOption}`,
                  })}`,
                }}
                className={css.exploreTutorButton}
              >
                <FormattedMessage id="OurCategories.exploreTutors" />
              </NamedLink>
            </div>
            {cat.image && <img src={cat.image} alt={cat.name} className={css.cardImage} />}
          </div>
        ))}
      </div>
    </section>
  );
}

export default OurCategories;
